import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import AppStack from '@atlassian/app-stack';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { PrimaryProductKeys } from '../../common/constants/primary-product-keys';
import { ProductKeys } from '../../common/constants/product-keys';
import { useProductConfigs } from '../../common/constants/products';
import {
	shouldShowRefreshedFilterRow,
	shouldShowSearchAppRefresh,
} from '../../common/utils/experiments';
import { usePrimaryProduct } from '../store/bootstrap';
import { messages } from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	iconBox: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		overflow: 'hidden',
		borderRadius: token('radius.small'),
		flexShrink: 0,
	},
});

type Props = {
	showGitHubIcon?: boolean;
};

/**
 * Renders a strip of 3 app icons: the primary 1P app icon (Confluence if Jira,
 * Jira otherwise), Google Drive (or GitHub), and Slack.
 */
export const AllAppsIcon = ({ showGitHubIcon = false }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const productConfigs = useProductConfigs();
	const showAppStack = useMemo(
		() =>
			(!showGitHubIcon && shouldShowSearchAppRefresh(primaryProduct)) ||
			shouldShowRefreshedFilterRow(primaryProduct),
		[primaryProduct, showGitHubIcon],
	);

	const FirstPartyIcon =
		primaryProduct === PrimaryProductKeys.Jira
			? productConfigs[ProductKeys.Confluence]?.Icon
			: productConfigs[ProductKeys.Jira]?.Icon;

	const icons = [
		FirstPartyIcon,
		showGitHubIcon
			? productConfigs[ProductKeys.Github]?.Icon
			: productConfigs[ProductKeys3P.GoogleDrive]?.Icon,
		productConfigs[ProductKeys3P.Slack]?.Icon,
	].filter(Boolean) as NonNullable<typeof FirstPartyIcon>[];

	if (showAppStack) {
		return (
			<Box role="presentation" aria-hidden="true">
				<AppStack
					data={icons.map((Icon) => ({ component: Icon }))}
					label={formatMessage(messages.appStackLabel)}
					maxCount={3}
					size="xxsmall"
				/>
			</Box>
		);
	}

	return (
		<Flex role="presentation" aria-hidden="true" xcss={styles.container}>
			{icons.map((Icon, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<Box key={index} xcss={styles.iconBox}>
					<Icon size="xxsmall" />
				</Box>
			))}
		</Flex>
	);
};
